"use client";

import { motion } from "framer-motion";
import { headerVariants, slideFromBottomVariants, cardVariants } from "@/lib/animations";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";

interface YachtFeature {
    key: string;
    label: string;
    value: string | number | boolean | null;
}

interface ProductYachtFeaturesProps {
    features: YachtFeature[];
    checkIn: string | null;
    checkOut: string | null;
    title: string | null;
    /** Ürün kodu / Yat ID — grid yerine üstte kompakt rozet */
    yachtId?: string | null;
}

/** Mobilde 2 sütunda dar hücrede kırılmayı azaltmak için tam genişlik satırı */
const LONG_VALUE_CHARS = 48;

export default function ProductYachtFeatures({
    features,
    checkIn,
    checkOut,
    title,
    yachtId,
}: ProductYachtFeaturesProps) {
    const { getVariable } = useLanguageVariables();
    const yachtIdLabel = getVariable('yacht_detail.yacht_id', 'Yacht ID');

    const hasGrid = features.length > 0;
    const hasYachtIdBadge = yachtId != null && String(yachtId).trim() !== '';

    if (!hasGrid && !hasYachtIdBadge && !checkIn && !checkOut) return null;

    return (
        <motion.section 
            id="features"
            className="mb-[60px] scroll-mt-20"
            variants={slideFromBottomVariants}
        >
            {title != "" &&
            <>
            
            <motion.h2 
                className="text-xl lg:text-3xl font-bold text-alsero-dark-gray mb-6"
                variants={headerVariants}
            >
                {title}
            </motion.h2>
            </>}

            {hasYachtIdBadge && (
                <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 sm:mb-4">
                    <span className="text-xs font-medium text-alsero-second/90 sm:text-sm">{yachtIdLabel}</span>
                    <span className="inline-flex max-w-full items-center rounded-md border border-[#e6d060] bg-[#f3cf3f] px-2.5 py-1 text-sm font-semibold tabular-nums text-alsero-second sm:px-3 sm:text-base">
                        {String(yachtId).trim()}
                    </span>
                </div>
            )}
            
            {hasGrid && (
                <motion.div 
                    className="mb-4 grid min-w-0 grid-cols-2 gap-3 sm:mb-8 sm:grid-cols-2 sm:gap-4 md:grid-cols-3 md:gap-6"
                    variants={cardVariants}
                >
                    {features.map((feature, index) => {
                        const isCheckInOut = feature.key === 'check_in' || feature.key === 'check_out';
                        const displayValue = typeof feature.value === 'boolean'
                            ? (feature.value ? 'Yes' : 'No')
                            : String(feature.value);
                        const spanFull =
                            displayValue.length > LONG_VALUE_CHARS
                                ? "col-span-2 md:col-span-1"
                                : "";
                        return (
                            <div
                                key={feature.key || index}
                                className={`min-w-0 rounded-lg border border-neutral-200/80 bg-gray-100 p-3 sm:p-4 ${spanFull}`}
                            >
                                <div className="mb-0.5 text-xs text-alsero-light-gray sm:mb-1 sm:text-sm">{feature.label}</div>
                                <div className={`break-words text-sm font-semibold text-alsero-dark-gray sm:text-base md:text-lg ${isCheckInOut ? 'flex items-center gap-1.5 sm:gap-2' : ''}`}>
                                    {isCheckInOut && (
                                        <svg className="h-4 w-4 shrink-0 text-alsero-first sm:h-5 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    )}
                                    {displayValue}
                                </div>
                            </div>
                        );
                    })}
                </motion.div>
            )}
        </motion.section>
    );
}
